 import React, {ReactElement} from 'react';
import List from "@mui/material/List/List";
import Drawer from "@mui/material/Drawer/Drawer";
import NavDrawerItem from "./NavDrawerItem";
import NavDrawerItemCollapseable from "./NavDrawerItemCollapseable";
import { MenuItem, DropdownNavLinkList } from "../../models/menu/Menu";
import { Divider, ListItemIcon, IconButton, ListItemButton, Tooltip} from '@mui/material';
import { Nullable } from '../../models/common/Nullable';
import { NavLink } from 'react-router-dom';
import ListItemText from "@mui/material/ListItemText/ListItemText";
import { RouteConfig } from '../../config/RouteConfig';
 import {
    DashboardOutlined, KeyboardDoubleArrowLeftOutlined,
 } from "@mui/icons-material";
 import {ViewTitleDisplay} from "./ViewTitleDisplay";
 import { MAIN_APP_TITLE} from "../../config/GeneralConfig";
 import {TextDisplay} from "../common/data-display/TextDisplay";
 import {NavDrawerLogoutItem} from "./NavDrawerLogoutItem";
 import {VerticalNavigationBar} from "./VerticalNavigationBar";
 import {ViewSettingsState} from "../../reducers/common/ViewSettingsReducer";
 import {sendUserToPage} from "../../actions/common/UserViewSettingsActions";
 import {UnsavedDataProtection} from "../common/dialogs/UnsavedDataProtection";
 import {NavigationLink} from "./NavLink";
 import {Language} from "../../config/LanguageConfig";

/**
 * typescript
 */
interface NavDrawerProps {
    navLinks: Nullable< MenuItem[] >
    isOpen: boolean
    isAppBarOpen: boolean
    viewTitle?: string | ReactElement
    toggleDrawer( open: boolean ): void
    viewSettingsState: ViewSettingsState
    sendUserToPage: typeof sendUserToPage
    language: Language
}
interface NavDrawerComponentState {
    isItemExpanded: { [ key: string ]: boolean }
    currentViewIdentifier: string
    unsavedDataProtectionOpen: boolean
    confirmNavDataProtectionAction: () => void,
}

 /**
  * Represents the navigation drawer component, providing hierarchical navigation for
  * users to easily access different views or sections of the application.
  *
  * This component dynamically builds and manages a menu structure, including navigation links
  * and collapsible dropdowns, to support hierarchical navigation. It also handles
  * unsaved data protection workflows when switching between navigation items.
  */
 class NavDrawer extends React.Component<NavDrawerProps, NavDrawerComponentState> {
    dropdownNavLinks: DropdownNavLinkList = {
        accounting: {
            name: "accounting",
            id: 68,
            title: () => this.props.language.common.invoices,
            hasChildren: false,
            childComponents: []
        },
        reporting: {
            name: "reporting",
            id: 95,
            title: () => this.props.language.common.reports,
            hasChildren: false,
            childComponents: []
        },
        admin: {
            name: "admin",
            id: 45,
            title: () => "Admin",
            link: RouteConfig.adminView.routes.main.path,
            hasChildren: false,
            childComponents: []
        }
    };

    state = {
        currentViewIdentifier: 'dashboard',
        unsavedDataProtectionOpen: false,
        confirmNavDataProtectionAction: () => {},
        isItemExpanded: {
            [this.dropdownNavLinks.reporting.name]: false,
            [this.dropdownNavLinks.accounting.name]: false,
            [this.dropdownNavLinks.admin.name]: false
        }
    };

    defaultDropdownLinks = () => {
        this.dropdownNavLinks.admin.childComponents = [];
        this.dropdownNavLinks.admin.hasChildren = false;
        this.dropdownNavLinks.accounting.childComponents = [];
        this.dropdownNavLinks.accounting.hasChildren = false;
        this.dropdownNavLinks.reporting.childComponents = [];
        this.dropdownNavLinks.reporting.hasChildren = false;
    };

    buildMenu( hideText: boolean = false ) {
        const { navLinks } = this.props;
        this.defaultDropdownLinks();
        let navComponents = [];

        if( navLinks !== null ) {

            navLinks.forEach( link => {
                //display link only if the view is set to be linked to via menu
                if( link.isMenuItem && link.parentViewId === 0 ) {

                    navComponents.push(
                       <NavDrawerItem
                           hideText={ hideText }
                           unsavedDataProtection={ this.props.viewSettingsState.unsavedDataProtection }
                           key={'nav-link-'+link.linkTitle.rawValue}
                           active={ this.state.currentViewIdentifier === link.linkTitle.rawValue }
                           onCLick={ ( _viewIdentifier, _link ) => this.onNavCLickHandler( _link, _viewIdentifier ) }
                           viewLink={link}
                       />
                    );
                } else {

                    switch (link.parentViewId) {
                        case this.dropdownNavLinks.accounting.id:
                            this.addNavChild(this.dropdownNavLinks.accounting.name, link, hideText);
                            break;
                        case this.dropdownNavLinks.reporting.id:
                            this.addNavChild(this.dropdownNavLinks.reporting.name, link, hideText);
                            break;
                        case this.dropdownNavLinks.admin.id:
                            this.addNavChild(this.dropdownNavLinks.admin.name, link, hideText );
                            break;
                    }
                }
            });

            if( this.dropdownNavLinks.accounting.hasChildren ) {
                navComponents.push(
                    this.addNavCollapseable( this.dropdownNavLinks.accounting.name, hideText )
                );
            }

            if( this.dropdownNavLinks.reporting.hasChildren ) {
                navComponents.push(
                    this.addNavCollapseable( this.dropdownNavLinks.reporting.name, hideText )
                );
            }

            if( this.dropdownNavLinks.admin.hasChildren ) {
                navComponents.push(
                    this.addNavCollapseable( this.dropdownNavLinks.admin.name, hideText )
                );
            }

        }
        return navComponents;
    };



    addNavCollapseable( name: string, hideText: boolean = false ): React.ReactNode {

        return (
            <NavDrawerItemCollapseable
                key={'nav-collapse-'+name}
                hideText={ hideText }
                name={name}
                onClick={ _viewIdentifier => this.setState({ ...this.state, currentViewIdentifier: _viewIdentifier }) }
                active={ this.state.currentViewIdentifier === name }
                viewLink={ this.dropdownNavLinks[ name ].link }
                title={this.dropdownNavLinks[name].title()}
                isExpanded={this.state.isItemExpanded[ name ]}
                toggleExpansion={this.toggleDrawerCollapse.bind( this )}
                childItems={this.dropdownNavLinks[name].childComponents}
            />
        );
    };
    

    addNavChild( name: string, link: MenuItem, hideText: boolean = false ) {
        this.dropdownNavLinks[name].hasChildren = true;
        this.dropdownNavLinks[name].childComponents.push(
            <NavDrawerItem
                key={'nav-link-'+link.linkTitle.rawValue}
                hideText={ hideText }
                viewLink={link}
                onCLick={ _viewIdentifier => this.setState({ ...this.state, currentViewIdentifier: _viewIdentifier }) }
                active={ this.state.currentViewIdentifier === link.linkTitle.rawValue }
            />
        );
    };

    toggleDrawerCollapse = ( expandableItem: string ) => {
        this.setState({
            isItemExpanded: {
                [expandableItem]: !(this.state.isItemExpanded[ expandableItem ])
            }
        });
    };


    onNavCLickHandler( route: string, viewIdentifier: string   ) {

        if( this.props.viewSettingsState.unsavedDataProtection ) {
            this.setState({
                ...this.state,
                unsavedDataProtectionOpen: true,
                confirmNavDataProtectionAction: () => {
                    this.setState({
                        ...this.state,
                        currentViewIdentifier: viewIdentifier,
                        unsavedDataProtectionOpen: false,
                        confirmNavDataProtectionAction: () => {}
                    });
                    this.props.sendUserToPage( route , viewIdentifier );

                }
            });
        } else {
            this.setState({
                ...this.state,
                currentViewIdentifier: viewIdentifier,
                unsavedDataProtectionOpen: false,
                confirmNavDataProtectionAction: () => {}
            });
        }

    }



    render() {
        const { isOpen, toggleDrawer, isAppBarOpen } = this.props;


        const isDashboardActive = 'dashboard' === this.state.currentViewIdentifier;
        let activeStyle = isDashboardActive
            ? "menu-border-active"
            : "menu-border"

        const colorScheme = isDashboardActive ? 'icon-active' : 'icon';

        return (
            <div>
                <VerticalNavigationBar isAppBarOpen={isAppBarOpen}>
                    <UnsavedDataProtection
                        isOpen={ this.state.unsavedDataProtectionOpen }
                        onYesClick={ this.state.confirmNavDataProtectionAction }
                        onNoClick={() => this.setState({  unsavedDataProtectionOpen: false, confirmNavDataProtectionAction: () => {} }) }
                    />
                    <div key={'nav-drawer-dashboard-ico'}
                         className={'nav-list-item nav-drawer-background '+ activeStyle}
                    >

                        <NavigationLink
                            unsavedDataProtection={ this.props.viewSettingsState.unsavedDataProtection }
                            to={RouteConfig.dashboard.routes.main.path}
                            onClick={ ()  => this.onNavCLickHandler( RouteConfig.dashboard.routes.main.path, "dashboard" )}
                        >
                            <Tooltip title={'Dashboard'} placement={'right-end'}>
                                <ListItemButton style={{display: "flex", height: '60px'}} className="menu-border">
                                    <ListItemIcon>
                                        <DashboardOutlined className={colorScheme} />
                                    </ListItemIcon>
                                </ListItemButton>
                            </Tooltip>

                        </NavigationLink>
                        <Divider/>

                    </div>
                    {this.buildMenu(true)}
                    <div style={{justifySelf: 'space-between'}}>
                        <NavDrawerLogoutItem hideText/>
                    </div>
                </VerticalNavigationBar>

                <Drawer
                    open={isOpen}
                    onClose={() => toggleDrawer(false)}
                >
                    <div
                        className="flex nav-drawer-header"
                        style={{
                            minHeight: 64 + 'px',
                            alignItems: 'center',
                            paddingLeft: '16px',
                            paddingRight: '16px'
                        }}>
                        <div
                            className="flex"
                            style={{minHeight: 64 + 'px', width: '90%', alignItems: 'center'}}
                        >
                            {
                                typeof this.props.viewTitle === "string"
                                    ? (
                                        <ViewTitleDisplay title={MAIN_APP_TITLE}/>
                                    )
                                    : this.props.viewTitle
                            }
                        </div>
                        <div className="flex" style={{width: '10%', minHeight: 64 + 'px', justifyContent: 'flex-end'}}>
                            <IconButton onClick={ () => toggleDrawer( false ) }>
                                <KeyboardDoubleArrowLeftOutlined style={{ color: 'white' }}  />
                            </IconButton>

                        </div>
                    </div>
                    <div
                        className="flex nav-drawer-background"
                        style={{ height: '60px', padding: '16px 8px 16px 8px', alignItems: 'center' }}
                    >
                        <TextDisplay variant="h6">
                            { this.props.language.common.mainMenu }
                        </TextDisplay>
                    </div>

                    <List className="nav-drawer-background">
                        <div key={'nav-drawer-dashboard'} className={'nav-list-item nav-drawer-background '+ activeStyle}>
                            <NavLink to={RouteConfig.dashboard.routes.main.path}
                                     onClick={() => this.setState({...this.state, currentViewIdentifier: 'dashboard'})}
                            >
                                <ListItemButton
                                    className={"nav-drawer-background "+ activeStyle}
                                    style={{display: "flex", height: '60px'}}
                                >
                                    <ListItemIcon>
                                        <DashboardOutlined className={colorScheme}/>
                                    </ListItemIcon>
                                    <ListItemText primary={'Dashboard'} className={colorScheme}/>
                                </ListItemButton>
                            </NavLink>
                            <Divider/>
                        </div>
                        {this.buildMenu()}
                    </List>
                    <div
                        className="flex nav-drawer-background"
                        style={{ height: '100%', flexDirection: 'column', justifyContent: 'flex-end' }}
                    >
                        <NavDrawerLogoutItem />
                    </div>
                </Drawer>
            </div>
        );
    }

}

 export default NavDrawer;